import { apiGet, apiPost, apiPut, apiDelete } from '../api/apiClient';
import { API_ENDPOINTS } from '@/constants/endpoints';
import {
  Folder,
  FolderListParams,
  FolderListResponse,
  CreateFolderRequest,
  UpdateFolderRequest,
  FolderActionResponse,
} from '@/constants/types/folder.types';

class FolderService {
  /**
   * Fetch folders with pagination, search, and sort
   */
  async getFolders(params?: FolderListParams): Promise<FolderListResponse> {
    try {
      const queryParams: Record<string, string> = {};

      if (params?.page) queryParams.page = params.page.toString();
      if (params?.limit) queryParams.limit = params.limit.toString();
      if (params?.search) queryParams.search = params.search;
      if (params?.sort) queryParams.sort = params.sort;
      if (params?.groupByParent !== undefined)
        queryParams.groupByParent = params.groupByParent.toString();

      const queryString = new URLSearchParams(queryParams).toString();
      const url = queryString
        ? `${API_ENDPOINTS.ADMIN.FOLDERS}?${queryString}`
        : API_ENDPOINTS.ADMIN.FOLDERS;

      return await apiGet<FolderListResponse>(url);
    } catch (error: any) {
      throw error.response?.data || { message: 'Failed to fetch folders' };
    }
  }

  /**
   * Create a new folder
   */
  async createFolder(data: CreateFolderRequest): Promise<FolderActionResponse> {
    try {
      return await apiPost<FolderActionResponse>(
        API_ENDPOINTS.ADMIN.FOLDERS,
        data
      );
    } catch (error: any) {
      throw error.response?.data || { message: 'Failed to create folder' };
    }
  }

  /**
   * Update an existing folder
   */
  async updateFolder(
    id: string,
    data: UpdateFolderRequest
  ): Promise<FolderActionResponse> {
    try {
      return await apiPut<FolderActionResponse>(
        API_ENDPOINTS.ADMIN.FOLDER_BY_ID(id),
        data
      );
    } catch (error: any) {
      throw error.response?.data || { message: 'Failed to update folder' };
    }
  }

  /**
   * Delete a folder
   */
  async deleteFolder(id: string): Promise<FolderActionResponse> {
    try {
      return await apiDelete<FolderActionResponse>(
        API_ENDPOINTS.ADMIN.FOLDER_BY_ID(id)
      );
    } catch (error: any) {
      throw error.response?.data || { message: 'Failed to delete folder' };
    }
  }

  /**
   * Get a single folder by ID (optional - for future use)
   */
  async getFolderById(id: string): Promise<{ success: boolean; data: { folder: Folder } }> {
    try {
      return await apiGet<{ success: boolean; data: { folder: Folder } }>(
        API_ENDPOINTS.ADMIN.FOLDER_BY_ID(id)
      );
    } catch (error: any) {
      throw error.response?.data || { message: 'Failed to fetch folder details' };
    }
  }
}

export const folderService = new FolderService();
